import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { useI18n } from "@/i18n";
import { Reveal, SectionHead } from "@/components/motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GALLERY } from "@/lib/gallery";

const Lightbox = ({ index, setIndex }) => {
  const { t } = useI18n();
  const g = t.gallery;
  const open = index !== null;
  const n = GALLERY.length;
  const go = useCallback((d) => setIndex((i) => (i + d + n) % n), [setIndex, n]);

  useEffect(() => {
    if (!open) return undefined;
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [open, go]);

  const item = open ? g.items[index] : null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && setIndex(null)}>
      <DialogContent data-lenis-prevent data-testid="gallery-lightbox" className="max-h-[92vh] w-[96vw] max-w-6xl overflow-y-auto border-white/15 bg-[#070C1A] p-0 text-white">
        {item && (
          <div className="grid lg:grid-cols-12">
            <div className="relative bg-black lg:col-span-8">
              <img key={index} src={GALLERY[index]} alt={item.t} className="aspect-[4/3] w-full object-cover" data-testid="lightbox-image" />
              <button onClick={() => go(-1)} aria-label={t.common.prev} data-testid="lightbox-prev" className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#070C1A]/80 p-3 hover:bg-[var(--gold)] hover:text-[var(--night)] transition-colors"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={() => go(1)} aria-label={t.common.next} data-testid="lightbox-next" className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#070C1A]/80 p-3 hover:bg-[var(--gold)] hover:text-[var(--night)] transition-colors"><ChevronRight className="h-5 w-5" /></button>
            </div>
            <div className="flex flex-col p-7 lg:col-span-4">
              <p className="font-mono text-xs text-[var(--gold)]" data-testid="lightbox-counter">{String(index + 1).padStart(2, "0")} / {n}</p>
              <DialogTitle className="font-display mt-3 text-3xl font-extrabold uppercase leading-tight" data-testid="lightbox-title">{item.t}</DialogTitle>
              <DialogDescription className="mt-3 text-[var(--steel)]">{item.d}</DialogDescription>
              <div className="mt-6 border-t border-white/10 pt-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--steel-dim)]">{g.visible}</p>
                <p className="mt-1 text-sm">{item.eq}</p>
              </div>
              <div className="mt-4 border-t border-white/10 pt-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--steel-dim)]">{g.spec}</p>
                <p className="mt-1 text-sm">{item.s}</p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const Gallery = () => {
  const { t } = useI18n();
  const g = t.gallery;
  const [index, setIndex] = useState(null);
  return (
    <section id="galerie" className="bg-[var(--navy)] py-24 lg:py-32" data-testid="gallery-section">
      <div className="container-x">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <SectionHead index="07" eyebrow={g.eyebrow} title={g.title} />
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-[var(--steel-dim)]">{g.hint}</p>
        </div>
        <div className="mt-14 grid grid-cols-2 gap-2 md:grid-cols-3 lg:gap-3">
          {GALLERY.map((src, i) => (
            <Reveal key={src} delay={(i % 3) * 0.05}>
              <button onClick={() => setIndex(i)} data-testid={`gallery-item-${i + 1}`} className="group relative block aspect-[4/3] w-full overflow-hidden bg-[#070C1A] text-left">
                <img src={src} alt={g.items[i].t} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-[1.07]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070C1A]/90 via-transparent to-transparent opacity-90" />
                <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-2 p-3 sm:p-4">
                  <p className="text-xs font-semibold sm:text-sm"><span className="mr-2 font-mono text-[var(--gold)]">{String(i + 1).padStart(2, "0")}</span>{g.items[i].t}</p>
                  <Maximize2 className="hidden h-4 w-4 shrink-0 text-[var(--gold)] opacity-0 transition-opacity group-hover:opacity-100 sm:block" />
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      <Lightbox index={index} setIndex={setIndex} />
    </section>
  );
};
